// Credit:
//  - https://stackoverflow.com/a/30753870
const focusableSelector = [
  "[contentEditable=true]",
  '[tabindex]:not([tabindex="-1"])',
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "iframe",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
]

export function trapFocus(
  node: HTMLElement,
  listener: (event: KeyboardEvent) => void
) {
  const modal = node.closest('[aria-modal="true"]') as HTMLElement
  if (modal) {
    requestAnimationFrame(() => modal.focus())
  }
  node.addEventListener("keydown", listener)
}

export function releaseFocus(
  node: HTMLElement,
  listener: (event: KeyboardEvent) => void
) {
  node.removeEventListener("keydown", listener)
}

/**
 * Finds all focusable elements in a container, takes into account shadow DOM if present
 * @param container - The main container element
 * @returns An array of focusable elements
 */
export function findAllFocusableElements(
  container: HTMLElement
): HTMLElement[] {
  const elements: HTMLElement[] = []

  const allElements = container.querySelectorAll("*")
  allElements.forEach((element) => {
    if (element.matches(focusableSelector.join(","))) {
      elements.push(element as HTMLElement)
    }
    if (element.shadowRoot) {
      const shadowElements = Array.from(
        element.shadowRoot.querySelectorAll(focusableSelector.join(","))
      ) as HTMLElement[]
      elements.push(...shadowElements)
    }
  })

  return elements
}

/**
 * Returns the deepest active element in the document or shadow root
 * @param doc - The document or shadow root to search in
 * @returns The deepest active element or null if no active element is found
 */
export function deepActiveElement(
  doc: Document | ShadowRoot = document
): Element | null {
  const active = doc.activeElement
  if (!active) return null
  if (active.shadowRoot) {
    return deepActiveElement(active.shadowRoot)
  }
  return active
}
